// @ts-nocheck
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAction,
  getOnboardingCandidates,
  updateUserProfile,
  getUserVariant,
  trackABTestEvent,
  saveOnboardingRecipes,
  generateSeedEmbedding,
} from "wasp/client/operations";
import { useUser } from "../../../hooks/useUser";
import { normalizeTasteProfile } from "../../lib/forms";
import { trackEvent } from "../../config/analytics";
import { COUNTRIES } from "../../../lib/countries";
import TasteProfileOnboardingFlowView from "./TasteProfileOnboardingFlow.view";
import { TastePairsOnboardingFlow } from "./TastePairsOnboardingFlow";
// Use Wasp actions for A/B testing to avoid importing server code in client.

// Business logic types
export interface TasteProfileData {
  // Region & Personalization (NEW - from NuraOnboarding)
  regionCountry: string;
  regionCurrency: string;
  reasons: string[]; // "Why are you here?" multi-selection

  // Dietary & Restrictions
  dietType: string;
  allergies: string[];
  dietaryRestrictions: string[];
  likes: string[];
  dislikes: string[];

  // Goals & Macros
  primaryGoal: string;
  macroTargets: {
    protein: number;
    carbs: number;
    fat: number;
    calories: number;
  };

  // Household & Equipment
  householdSize: number;
  availableEquipment: string[];
  batchPrepPreference: string;

  // Preferences & Pantry
  spiceLevel: string;
  cookingTime: string;
  pantryStaples: string[];
  favoriteCuisines: string[];
  favoriteIngredients: string[];

  // Meal Planning Preferences (NEW)
  mealTypes: string[]; // breakfast, lunch, dinner
  budgetRange: string; // economy, moderate, premium
  cookingDifficulty: string; // beginner, intermediate, advanced

  // Units & Measurements
  unitSystem: "imperial" | "metric";
  temperatureUnit: "fahrenheit" | "celsius";
  volumeUnit: string;
  weightUnit: string;

  // Notifications & Email
  emailPreferences: {
    weeklyDigest: boolean;
    recipeSuggestions: boolean;
    shoppingReminders: boolean;
    seasonalUpdates: boolean;
  };
  notificationSettings: {
    pushNotifications: boolean;
    emailNotifications: boolean;
    smsNotifications: boolean;
  };

  // Recipe DNA
  tasteProfile: {
    spiceTolerance: number;
    sweetnessPreference: number;
    complexityLevel: number;
    texturePreferences: string[];
    cookingMethods: string[];
  };

  // Metadata
  completedSteps: string[];
  lastUpdated: Date;
}

interface TasteProfileOnboardingFlowContainerProps {
  onComplete: (data: TasteProfileData) => void;
  initialData?: Partial<TasteProfileData>;
}

/**
 * TasteProfileOnboardingFlow Container - Centralizes all business logic for 8-step onboarding
 *
 * This centralizes:
 * - Wasp operations and user management (updateUserProfile, useUser)
 * - Multi-step form state management (8 steps total)
 * - Complex form validation logic (step-by-step validation)
 * - Form data normalization and persistence
 * - Analytics tracking throughout the user journey
 * - Profile updating and completion logic
 * - All event handlers and step navigation
 */
export default function TasteProfileOnboardingFlowContainer({
  onComplete,
  initialData,
}: TasteProfileOnboardingFlowContainerProps) {
  // Wasp hooks for data and user management
  const navigate = useNavigate();
  const { user } = useUser();
  const updateProfile = useAction(updateUserProfile);
  const doGetVariant = useAction(getUserVariant);
  const trackAbEvent = useAction(trackABTestEvent);
  const saveRecipes = useAction(saveOnboardingRecipes);

  // Business logic state - Form and step management
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<TasteProfileData>(
    normalizeTasteProfile(initialData || {}),
  );
  const [abVariant, setAbVariant] = useState<string | null>(null);
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<string[]>([]); // For Step 6 recipe selection

  // Business logic constants
  const totalSteps = 6;

  // A/B Testing Logic (server-driven via actions) - call once per session
  const variantRequested = useRef(false);
  const VARIANT_CACHE_KEY = "ab:onboarding-flow";
  useEffect(() => {
    if (!user?.id) return;
    if (variantRequested.current) return;
    // Session cache: if present, skip network
    try {
      const cached =
        typeof window !== "undefined"
          ? window.sessionStorage.getItem(VARIANT_CACHE_KEY)
          : null;
      if (cached) {
        setAbVariant(cached);
        variantRequested.current = true;
        return;
      }
    } catch {}
    variantRequested.current = true;
    (async () => {
      try {
        const variant = await doGetVariant({ testId: "onboarding-flow" });
        const assignedVariant = variant || "control";
        setAbVariant(assignedVariant);
        try {
          if (typeof window !== "undefined")
            window.sessionStorage.setItem(VARIANT_CACHE_KEY, assignedVariant);
        } catch {}
        try {
          trackEvent("ab_test_assigned", {
            testId: "onboarding-flow",
            variant: assignedVariant,
          });
        } catch {}
        try {
          await trackAbEvent({
            testId: "onboarding-flow",
            eventType: "ab_test_assigned",
            metadata: { variant: assignedVariant },
          });
        } catch {}
      } catch {
        try {
          trackEvent("ab_test_assignment_failed", {
            testId: "onboarding-flow",
          });
        } catch {}
      }
    })();
  }, [user?.id]);

  // Business logic: Analytics tracking on step changes
  useEffect(() => {
    if (user?.id) {
      trackEvent("taste_profile_onboarding_start", {
        userId: user.id,
        step: currentStep,
      });
    }
  }, [currentStep, user?.id]);

  // Business logic: Form data updates
  const updateFormData = (updates: Partial<TasteProfileData>) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
      lastUpdated: new Date(),
    }));
  };

  // Business logic: Recipe selection (Step 6) - max 3
  const handleRecipeSelect = (recipeId: string) => {
    setSelectedRecipeIds((prev) => {
      if (prev.includes(recipeId)) {
        // Deselect if already selected
        return prev.filter((id) => id !== recipeId);
      } else if (prev.length < 3) {
        // Add if less than 3 selected
        return [...prev, recipeId];
      }
      // Do nothing if already 3 selected and trying to add another
      return prev;
    });
  };

  // Business logic: Step navigation handlers
  const handleNext = () => {
    if (currentStep < totalSteps) {
      const newCompletedSteps = [
        ...(formData.completedSteps ?? []),
        currentStep.toString(),
      ];
      updateFormData({ completedSteps: newCompletedSteps });
      setCurrentStep(currentStep + 1);
      try {
        if (typeof window !== "undefined") {
          // Scroll to top on step change to ensure consistent UX
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
      } catch {}
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      try {
        if (typeof window !== "undefined") {
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
      } catch {}
    }
  };

  const handleSkip = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      try {
        if (typeof window !== "undefined") {
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
      } catch {}
    }
  };

  // Ensure Step 6 recipe selection starts clean (0/3)
  useEffect(() => {
    if (currentStep === 6 && selectedRecipeIds.length !== 0) {
      setSelectedRecipeIds([]);
    }
  }, [currentStep]);

  // Business logic: Complex step validation
  const isStepValid = () => {
    switch (currentStep) {
      case 1: // Essentials (Region & Household Size)
        return (
          !!formData.regionCountry &&
          formData.householdSize &&
          formData.householdSize > 0
        );
      case 2: // Diet & Goals
        return !!formData.dietType && !!formData.primaryGoal;
      case 3: // Kitchen & Equipment
        return true; // Optional, but encouraged
      case 4: // Taste Preferences
        return true; // Optional preferences
      case 5: // Review & Confirm
        return true;
      case 6: // Your Recipes (recommendation selection)
        return true;
      default:
        return false;
    }
  };

  // Business logic: Complex completion handler with Wasp integration
  const handleComplete = async (recipeIds?: string[]) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (user?.id) {
        trackEvent("taste_profile_onboarding_complete", {
          userId: user.id,
          completedSteps: formData.completedSteps?.length ?? 0,
          totalSteps,
        });
        try {
          await trackAbEvent({
            testId: "onboarding-flow",
            eventType: "onboarding_completed",
            metadata: {
              completedSteps: formData.completedSteps?.length ?? 0,
              totalSteps,
              variant: abVariant || "control",
            },
          });
        } catch {}
      }

      // Update user profile with taste profile data via Wasp
      await updateProfile({
        // Legacy fields (for backward compatibility)
        dietaryPreferences: formData.dietaryRestrictions,
        avoids: formData.dislikes,
        
        // NEW: Properly validated fields
        allergies: formData.allergies || [],
        dietaryRestrictions: formData.dietaryRestrictions || [],
        favoriteCuisines: formData.favoriteCuisines || [],
        
        // Other fields
        spiceTolerance: formData.spiceLevel,
        goals: formData.primaryGoal ? [formData.primaryGoal] : [],
        householdSize: formData.householdSize,
        kitchenEquipment: formData.availableEquipment,
        flavorPreferences: formData.favoriteCuisines,
        prepTime: formData.cookingTime,
        location: `${formData.regionCountry}:${formData.regionCurrency}`,
        preferredMealTypes: formData.mealTypes,
        budgetRange: formData.budgetRange,
        cookingDifficulty: formData.cookingDifficulty,
        weeklyDigestOptIn: Boolean(formData.emailPreferences?.weeklyDigest),
        onboardingComplete: true,
      });

      // Generate seed embedding in background
      try {
        await generateSeedEmbedding({});
      } catch (error) {
        console.error("Failed to generate seed embedding:", error);
      }

      // Auto-save selected recipes to user's favorites (use selectedRecipeIds from Step 6)
      const idsToSave = recipeIds && recipeIds.length > 0 ? recipeIds : selectedRecipeIds;
      if (idsToSave && idsToSave.length > 0) {
        try {
          await saveRecipes({ recipeIds: idsToSave });
          console.log(`Auto-saved ${idsToSave.length} recipes to favorites`);
        } catch (error) {
          console.error("Failed to save onboarding recipes:", error);
        }
      }

      const completedData = normalizeTasteProfile({
        ...formData,
        completedSteps: [...formData.completedSteps, currentStep.toString()],
        lastUpdated: new Date(),
      });

      onComplete(completedData);
    } catch (error) {
      console.error("Error saving taste profile:", error);
      alert("There was an error saving your profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Business logic: Step-specific choice options and configurations
  const dietTypes = [
    {
      id: "omnivore",
      emoji: "🍖",
      title: "Omnivore",
      description: "Eat everything - meat, dairy, plants",
    },
    {
      id: "vegetarian",
      emoji: "🥕",
      title: "Vegetarian",
      description: "No meat, but dairy and eggs are ok",
    },
    {
      id: "vegan",
      emoji: "🌱",
      title: "Vegan",
      description: "No animal products at all",
    },
    {
      id: "pescatarian",
      emoji: "🐟",
      title: "Pescatarian",
      description: "Vegetarian + fish and seafood",
    },
    {
      id: "keto",
      emoji: "🥑",
      title: "Keto",
      description: "Low carb, high fat",
    },
    {
      id: "paleo",
      emoji: "🍗",
      title: "Paleo",
      description: "Whole foods, no processed items",
    },
    {
      id: "mediterranean",
      emoji: "🫒",
      title: "Mediterranean",
      description: "Heart-healthy, olive oil focused",
    },
    {
      id: "flexitarian",
      emoji: "🥗",
      title: "Flexitarian",
      description: "Mostly plant-based with occasional meat",
    },
  ];

  const commonAllergies = [
    "nuts",
    "dairy",
    "eggs",
    "gluten",
    "soy",
    "shellfish",
    "fish",
    "sesame",
    "sulfites",
  ];

  const commonRestrictions = [
    "halal",
    "kosher",
    "low-sodium",
    "low-sugar",
    "low-FODMAP",
    "nightshade-free",
  ];

  const goals = [
    // Enhanced goals from NuraOnboarding integration
    {
      id: "quick",
      emoji: "⏱️",
      title: "Quick meals",
      description: "Fast, simple recipes under 30 minutes",
    },
    {
      id: "balanced",
      emoji: "🥗",
      title: "Healthy balance",
      description: "Balanced nutrition for sustained energy",
    },
    {
      id: "budget",
      emoji: "💸",
      title: "Budget friendly",
      description: "Cost-effective, economical meal planning",
    },
    {
      id: "protein",
      emoji: "💪",
      title: "High protein",
      description: "Protein-rich meals for fitness goals",
    },
    // Existing comprehensive goals
    {
      id: "weight-loss",
      emoji: "⚖️",
      title: "Weight Loss",
      description: "Calorie-controlled, portion-focused meals",
    },
    {
      id: "weight-gain",
      emoji: "🏋️",
      title: "Weight Gain",
      description: "Calorie-dense, muscle-building meals",
    },
    {
      id: "meal-prep",
      emoji: "🍱",
      title: "Meal Prep",
      description: "Batch cooking and food prep focused",
    },
    {
      id: "variety",
      emoji: "🌍",
      title: "Try New Things",
      description: "Diverse cuisines and new ingredients",
    },
    {
      id: "family-friendly",
      emoji: "👨‍👩‍👧‍👦",
      title: "Family Friendly",
      description: "Kid-approved, crowd-pleasing meals",
    },
  ];

  const equipment = [
    { id: "stovetop", emoji: "🍳", title: "Stovetop" },
    { id: "oven", emoji: "🔥", title: "Oven" },
    { id: "microwave", emoji: "⚡", title: "Microwave" },
    { id: "slow-cooker", emoji: "🍲", title: "Slow Cooker" },
    { id: "instant-pot", emoji: "🏃", title: "Instant Pot" },
    { id: "air-fryer", emoji: "💨", title: "Air Fryer" },
    { id: "blender", emoji: "🌪️", title: "Blender" },
    { id: "food-processor", emoji: "⚙️", title: "Food Processor" },
    { id: "grill", emoji: "🔥", title: "Grill/BBQ" },
    { id: "wok", emoji: "🥘", title: "Wok" },
  ];

  const batchPreferences = [
    {
      id: "individual",
      emoji: "🍽️",
      title: "Individual Portions",
      description: "Single servings, fresh daily",
    },
    {
      id: "small-batch",
      emoji: "🍱",
      title: "Small Batch",
      description: "2-3 servings, 2-3 days",
    },
    {
      id: "large-batch",
      emoji: "🏭",
      title: "Large Batch",
      description: "4+ servings, full week prep",
    },
  ];

  const spiceLevels = [
    {
      id: "mild",
      emoji: "😊",
      title: "Mild",
      description: "No heat, family-friendly",
    },
    {
      id: "medium",
      emoji: "🌶️",
      title: "Medium",
      description: "Some kick, but not overwhelming",
    },
    { id: "hot", emoji: "🔥", title: "Hot", description: "Love the burn!" },
    {
      id: "very-hot",
      emoji: "💥",
      title: "Very Hot",
      description: "Bring on the heat!",
    },
  ];

  const cookingTimes = [
    { id: "15min", title: "Under 15 minutes", description: "Quick assembly" },
    { id: "30min", title: "15-30 minutes", description: "Fast cooking" },
    { id: "45min", title: "30-45 minutes", description: "Moderate cooking" },
    { id: "60min", title: "45-60 minutes", description: "Relaxed cooking" },
    {
      id: "90min",
      title: "Over 60 minutes",
      description: "Slow cooking projects",
    },
  ];

  const cuisines = [
    "Italian",
    "Mexican",
    "Chinese",
    "Indian",
    "Thai",
    "Japanese",
    "Mediterranean",
    "American",
    "French",
    "Korean",
    "Vietnamese",
    "Middle Eastern",
    "Caribbean",
  ];

  const pantryStaples = [
    "olive oil",
    "garlic",
    "onions",
    "salt",
    "pepper",
    "pasta",
    "rice",
    "beans",
    "canned tomatoes",
    "chicken broth",
    "soy sauce",
    "honey",
    "flour",
    "eggs",
  ];

  const texturePreferences = [
    "crispy",
    "creamy",
    "chewy",
    "tender",
    "juicy",
    "flaky",
    "smooth",
    "chunky",
  ];

  const cookingMethods = [
    "grilled",
    "baked",
    "roasted",
    "sautéed",
    "fried",
    "steamed",
    "boiled",
    "raw",
  ];

  // NEW: Meal Planning Configuration Options
  const mealTypeOptions = [
    {
      id: "breakfast",
      icon: "Coffee",
      title: "Breakfast",
      description: "Start your day right",
    },
    {
      id: "lunch",
      icon: "Sun",
      title: "Lunch",
      description: "Midday fuel",
    },
    {
      id: "dinner",
      icon: "Moon",
      title: "Dinner",
      description: "Evening comfort",
    },
    {
      id: "snacks",
      icon: "Apple",
      title: "Snacks",
      description: "Light bites",
    },
  ];

  const budgetOptions = [
    {
      id: "economy",
      icon: "DollarSign",
      title: "Economy",
      description: "Budget-friendly, under $5 per serving",
    },
    {
      id: "moderate",
      icon: "TrendingUp",
      title: "Moderate",
      description: "Balanced cost, $5-10 per serving",
    },
    {
      id: "premium",
      icon: "Star",
      title: "Premium",
      description: "High-quality ingredients, $10+ per serving",
    },
  ];

  const difficultyOptions = [
    {
      id: "beginner",
      icon: "Smile",
      title: "Beginner",
      description: "Simple recipes, basic techniques",
    },
    {
      id: "intermediate",
      icon: "ChefHat",
      title: "Intermediate",
      description: "Moderate complexity, some skill required",
    },
    {
      id: "advanced",
      icon: "Award",
      title: "Advanced",
      description: "Complex recipes, advanced techniques",
    },
  ];

  const emailPreferenceOptions = [
    {
      key: "weeklyDigest",
      label: "Weekly meal planning digest",
      description: "Recipe ideas and tips",
    },
    {
      key: "recipeSuggestions",
      label: "Personalized recipe suggestions",
      description: "Based on your taste profile",
    },
    {
      key: "shoppingReminders",
      label: "Shopping list reminders",
      description: "When ingredients are running low",
    },
    {
      key: "seasonalUpdates",
      label: "Seasonal recipe updates",
      description: "Fresh ideas for each season",
    },
  ];

  const notificationOptions = [
    {
      key: "pushNotifications",
      label: "Push Notifications",
      description: "Get notified about meal plan updates",
    },
    {
      key: "emailNotifications",
      label: "Email Notifications",
      description: "Important updates via email",
    },
    {
      key: "smsNotifications",
      label: "SMS Notifications",
      description: "Text message reminders",
    },
  ];

  // NEW: Enhanced configurations from NuraOnboarding integration
  const reasonOptions = [
    "Eat healthier",
    "Save time on dinner",
    "Plan for my family",
    "High‑protein / fitness",
    "Manage dietary needs",
    "Stick to a budget",
    "Explore new meals",
    "Other",
  ];

  const householdOptions = [
    { id: 1, label: "Just me" },
    { id: 2, label: "2 people" },
    { id: 3, label: "3 people" },
    { id: 4, label: "4 people" },
    { id: 5, label: "5 people" },
    { id: 6, label: "6+ people" },
  ];

  // Map all props to the view
  const viewProps = {
    // Data
    formData,
    currentStep,
    totalSteps,
    isSubmitting,

    // Computed values
    isStepValid: isStepValid(),

    // Configuration options
    dietTypes,
    commonAllergies,
    commonRestrictions,
    goals,
    equipment,
    batchPreferences,
    spiceLevels,
    cookingTimes,
    cuisines,
    pantryStaples,
    texturePreferences,
    cookingMethods,
    emailPreferenceOptions,
    notificationOptions,

    // NEW: Enhanced configuration options from NuraOnboarding
    countries: COUNTRIES,
    reasonOptions,
    householdOptions,

    // NEW: Meal Planning Configuration Options
    mealTypeOptions,
    budgetOptions,
    difficultyOptions,

    // Event handlers - Form updates
    updateFormData,

    // Event handlers - Navigation
    onNext: handleNext,
    onBack: handleBack,
    onSkip: handleSkip,
    onComplete: handleComplete,

    // Recipe selection for Step 6
    selectedRecipeIds,
    onRecipeSelect: handleRecipeSelect,
  };

  // Prepare preferences and candidate gating hooks before any early returns
  // Memoize to provide stable identity to downstream components/effects
  const preferences = useMemo(
    () => ({
      cuisines: formData.favoriteCuisines || [],
      dietaryRestrictions: formData.dietaryRestrictions || [],
      cookingSkillLevel: formData.cookingDifficulty || "intermediate",
      spiceLevel: formData.spiceLevel,
      householdSize: formData.householdSize || 2,
    }),
    [
      formData.favoriteCuisines,
      formData.dietaryRestrictions,
      formData.cookingDifficulty,
      formData.spiceLevel,
      formData.householdSize,
    ],
  );
  // Render the 6-step onboarding flow view (Step 6 now shows PostOnboardingRecipeCarousel)
  return <TasteProfileOnboardingFlowView {...viewProps} />;
}

// Types are exported via the wrapper component file
